import { useState, useCallback, useMemo } from 'react';
import type { ReadingTag, TagCategory } from '../types/collections';
import type { TarotReading } from '../types/tarot';

// Mock data for tags - in a real app this would come from API
const mockTags: ReadingTag[] = [
  { id: '1', name: 'love', color: '#ef4444', usageCount: 15, createdAt: new Date().toISOString() },
  { id: '2', name: 'career', color: '#10b981', usageCount: 8, createdAt: new Date().toISOString() },
  { id: '3', name: 'spiritual', color: '#8b5cf6', usageCount: 12, createdAt: new Date().toISOString() },
  { id: '4', name: 'guidance', color: '#f59e0b', usageCount: 20, createdAt: new Date().toISOString() },
  { id: '5', name: 'decision', color: '#06b6d4', usageCount: 6, createdAt: new Date().toISOString() },
  { id: '6', name: 'relationship', color: '#ec4899', usageCount: 11, createdAt: new Date().toISOString() },
  { id: '7', name: 'future', color: '#6366f1', usageCount: 9, createdAt: new Date().toISOString() },
  { id: '8', name: 'past', color: '#84cc16', usageCount: 4, createdAt: new Date().toISOString() }
];

const mockCategories: TagCategory[] = [
  {
    id: '1',
    name: 'Life Areas',
    color: '#3b82f6',
    tags: ['love', 'career', 'health', 'family']
  },
  {
    id: '2',
    name: 'Time Focus',
    color: '#10b981',
    tags: ['past', 'present', 'future']
  },
  {
    id: '3',
    name: 'Reading Type',
    color: '#8b5cf6',
    tags: ['guidance', 'decision', 'spiritual', 'insight']
  }
];

export function useTagManagement(readings: TarotReading[] = []) {
  const [tags, setTags] = useState<ReadingTag[]>(mockTags);
  const [categories, setCategories] = useState<TagCategory[]>(mockCategories);

  // Calculate actual usage counts from readings
  const tagsWithUsage = useMemo(() => {
    const usageCounts = new Map<string, number>();
    
    readings.forEach(reading => {
      reading.tags.forEach(tag => {
        usageCounts.set(tag, (usageCounts.get(tag) || 0) + 1);
      });
    });

    return tags.map(tag => ({
      ...tag,
      usageCount: usageCounts.get(tag.name) || 0
    }));
  }, [tags, readings]);

  const createTag = useCallback((tag: Omit<ReadingTag, 'id' | 'usageCount' | 'createdAt'>) => {
    const newTag: ReadingTag = {
      ...tag,
      id: Date.now().toString(),
      usageCount: 0,
      createdAt: new Date().toISOString()
    };
    setTags(prev => [...prev, newTag]);
    return newTag;
  }, []);

  const updateTag = useCallback((id: string, updates: Partial<ReadingTag>) => {
    setTags(prev => prev.map(tag => 
      tag.id === id ? { ...tag, ...updates } : tag
    ));
  }, []);

  const deleteTag = useCallback((id: string) => {
    setTags(prev => prev.filter(tag => tag.id !== id));
  }, []);

  const createCategory = useCallback((category: Omit<TagCategory, 'id'>) => {
    const newCategory: TagCategory = {
      ...category,
      id: Date.now().toString()
    };
    setCategories(prev => [...prev, newCategory]);
    return newCategory;
  }, []);

  const updateCategory = useCallback((id: string, updates: Partial<TagCategory>) => {
    setCategories(prev => prev.map(category => 
      category.id === id ? { ...category, ...updates } : category
    ));
  }, []);

  const deleteCategory = useCallback((id: string) => {
    setCategories(prev => prev.filter(category => category.id !== id));
  }, []);

  const getTagsByCategory = useCallback((categoryId: string) => {
    const category = categories.find(c => c.id === categoryId);
    if (!category) return [];
    
    return tagsWithUsage.filter(tag => category.tags.includes(tag.name));
  }, [categories, tagsWithUsage]);

  const getPopularTags = useCallback((limit: number = 10) => {
    return [...tagsWithUsage]
      .sort((a, b) => b.usageCount - a.usageCount)
      .slice(0, limit);
  }, [tagsWithUsage]);

  const searchTags = useCallback((query: string) => {
    const lowercaseQuery = query.toLowerCase();
    return tagsWithUsage.filter(tag => 
      tag.name.toLowerCase().includes(lowercaseQuery) ||
      tag.description?.toLowerCase().includes(lowercaseQuery)
    );
  }, [tagsWithUsage]);

  const suggestTags = useCallback((readingText: string) => {
    const lowercaseText = readingText.toLowerCase();
    const suggestions = tagsWithUsage.filter(tag => {
      const tagName = tag.name.toLowerCase();
      return lowercaseText.includes(tagName) || 
             (tag.description && lowercaseText.includes(tag.description.toLowerCase()));
    });
    
    return suggestions.slice(0, 5); // Return top 5 suggestions
  }, [tagsWithUsage]);

  return {
    tags: tagsWithUsage,
    categories,
    createTag,
    updateTag,
    deleteTag,
    createCategory,
    updateCategory,
    deleteCategory,
    getTagsByCategory,
    getPopularTags,
    searchTags,
    suggestTags
  };
}